import { mapState } from 'vuex'
import { find, isEmpty, some } from 'lodash'
import ChartDataService from '@/services/ChartDataService'
import UsingColorPalette from '@/mixins/UsingColorPalette'
import ChartConfigurations from '@/components/list-view-visualisation/chart/ChartConfigurations'

export default {
  mixins: [UsingColorPalette],
  props: {
    listId: String,
    category: String,
    dateFormat: String,
    level: [String, Number],
    filterQuery: String,
    measures: Array,
    calculatedMeasures: Array,
    selectedSeries: String,
    serieslevel: String,
    smartUrlPrefix: String,
  },
  data() {
    return {
      chartData: { type: Object, default: () => ({}) },
      isFullyRendered: false,
    }
  },
  computed: {
    chartOptions() {
      return {
        grid: {
          bottom: 0,
          left: '7%',
          right: '3%',
          containLabel: true,
        },
        xAxis: {
          boundaryGap: true,
          axisLabel: {
            formatter: this.formatter,
            rotate: 45,
          },
          data: this.chartData.categories,
        },
        series: this.seriesWithChartColors(),
        legend: {
          type: this.getLegendType('series'),
        },
      }
    },
    isEmbedded() {
      return this.$route.query.embedded === 'true'
    },
    ...mapState({
      categories: (state) => state.ChartCategory.categories,
      placeHolderMessage: (state) => state.ChartCategory.placeHolderMessage,
      isFillMissingDates: (state) => state.ChartCategory.isFillMissingDates,
    }),
  },
  methods: {
    seriesWithChartColors() {
      if (this.chartData.series && this.colorPalette.length > 0) {
        const colorCount = this.colorPalette.length
        this.chartData.series.forEach((series, index) => {
          series.color = this.colorPalette[index % colorCount]
        })
      }
      return this.chartData.series
    },
    getAvailableWidth() {
      return this.$parent.$el.clientWidth
    },
    // This has the potential of being totally unreliable ¯\_(ツ)_/¯
    getLegendType(legendAttribute) {
      // Each legend icon takes up a specific amount of space
      const legendLabelWidth = 30
      // Assuming each letter takes some pixels on average
      const letterWidth = 8
      // Max number of legend lines we want to see
      const maxLegendLines = 3

      if (this.chartData[legendAttribute] && this.chartData[legendAttribute].length) {
        const elementCount = this.chartData[legendAttribute].length
        const totalCharacters = this.chartData[legendAttribute].reduce(
          (numOfChars, attribute) => numOfChars + (attribute.length || attribute.name.length),
          0,
        )

        return elementCount * legendLabelWidth + totalCharacters * letterWidth >=
          maxLegendLines * this.getAvailableWidth()
          ? 'scroll'
          : 'plain'
      }
      return 'plain'
    },
    formatter(value) {
      const maxLength = 30
      if (value.length > maxLength) {
        return `${value.slice(0, maxLength)}...`
      }
      return `${value}`
    },
    async loadChartData(
      listId,
      category,
      dateFormat,
      level,
      measures,
      calculatedMeasures,
      series,
      serieslevel,
      searchAndFilterParams,
      isFillMissingDates,
    ) {
      try {
        this.chartData = (
          await ChartDataService.getChartData(
            listId,
            category,
            dateFormat,
            level,
            measures,
            calculatedMeasures,
            series,
            serieslevel,
            searchAndFilterParams,
            isFillMissingDates,
          )
        ).data
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        const content = window.parent
        content.postMessage('vue-loaded', window.location.origin)
      }
    },
    getJsonObject(queryString) {
      if (!isEmpty(queryString)) {
        return JSON.parse(queryString)
      }
      return {}
    },
    setPlaceHolderMessage() {
      if (!this.isEmbedded && isEmpty(this.categories)) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.cannotbecharted'))
      } else if (!isEmpty(this.chartData.placeHolderMessage)) {
        this.$store.dispatch('setPlaceHolderMessage', this.chartData.placeHolderMessage)
      } else if (some(this.chartData, isEmpty)) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.nodata'))
      } else if (
        this.category === this.selectedSeries &&
        find(ChartConfigurations, (config) => config.component.name === this.$options.name)
          .showSeries
      ) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.invalidseriesselection'))
      }
    },
    emitLoaded() {
      this.setPlaceHolderMessage()
      this.$emit('loaded', isEmpty(this.placeHolderMessage))
      // Default animation duration + 100ms
      setTimeout(() => {
        this.isFullyRendered = true
      }, 1100)
    },
    loadData() {
      this.$store.dispatch('setPlaceHolderMessage', '')
      this.loadChartData(
        this.listId,
        this.category,
        this.dateFormat,
        this.level,
        this.measures,
        this.calculatedMeasures,
        this.selectedSeries,
        this.serieslevel,
        this.getJsonObject(this.filterQuery),
        this.isFillMissingDates,
      )
    },
  },

  updated() {
    this.$nextTick(this.emitLoaded)
  },

  mounted() {
    this.loadData()
  },
}
